import React, { useEffect, useState } from 'react';
import { FaCheck } from 'react-icons/fa';
import { readAttribution } from '../utils/attribution';
import './AuditLandingPage.css';

export const AUDIT_ENDPOINT = 'https://audit.ubotika.site/submit';

export const BUSINESS_TYPES = [
  'Contractor / Home services',
  'Salon / Spa / Med-spa',
  'Restaurant / Bar',
  'Medical / Dental / Clinic',
  'Real estate / Property',
  'Law firm',
  'Auto / Repair shop',
  'Other',
];

// Reusable lead-capture form. Fires the `audit_form_submit` conversion event on
// success, tagged with `src` so we can see which page/landing the lead came from.
function AuditForm({ src = 'direct' }) {
  const [website, setWebsite] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [smsConsent, setSmsConsent] = useState(false);
  const [businessType, setBusinessType] = useState('');
  const [hp, setHp] = useState('');
  const [status, setStatus] = useState('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const validate = () => {
    if (hp) return 'bot';
    if (!website.trim()) return 'Please enter your business website.';
    // A real site has a domain and an ending (yourbusiness.com) — not an email
    // address or a bare word. Mirrors the server-side check in audit-webhook.
    const host = website.trim().replace(/^https?:\/\//i, '').split(/[/?#]/)[0];
    if (host.includes('@') || !/^([a-z0-9-]+\.)+[a-z]{2,}$/i.test(host)) {
      return 'Please enter your website address, like yourbusiness.com.';
    }
    if (!/^\S+@\S+\.\S+$/.test(email)) return 'Please enter a valid email address.';
    if (!businessType) return 'Please pick your business type.';
    if (!/^[2-9]\d{9}$/.test(phone.replace(/\D/g, '').replace(/^1(?=\d{10}$)/, ''))) {
      return 'Please enter a valid 10-digit phone number so we can reach you about the audit.';
    }
    return null;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err === 'bot') return;
    if (err) { setErrorMsg(err); return; }

    setStatus('sending');
    setErrorMsg('');

    let normalizedSite = website.trim();
    if (!/^https?:\/\//i.test(normalizedSite)) {
      normalizedSite = `https://${normalizedSite}`;
    }

    try {
      const res = await fetch(AUDIT_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          website: normalizedSite,
          email: email.trim(),
          businessType,
          source: src,
          phone: phone.trim(),
          smsConsent,
          landingPath: window.location.pathname,
          utm: readAttribution(),
        }),
      });
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      setStatus('success');
    } catch (err) {
      setStatus('error');
      setErrorMsg(
        'Submission failed. Please email hello@ubotika.com with your website and we\'ll send your audit manually.'
      );
    }
  };

  useEffect(() => {
    if (status === 'success' && typeof window !== 'undefined' && window.gtag) {
      // GA4 lead event
      window.gtag('event', 'audit_form_submit', { source: src });
      // Google Ads "Submit lead form" conversion — fired at submit time, when
      // window.gtag is the fully-loaded gtag.js (verified to fire in-browser).
      window.gtag('event', 'conversion', {
        send_to: 'AW-18245536456/Pz-jCJ6LtsAcEMiVk_xD',
      });
    }
    if (status === 'success' && typeof window !== 'undefined' && window.fbq) {
      window.fbq('track', 'Lead', { content_name: 'audit_form_submit', location: src });
    }
  }, [status, src]);

  if (status === 'success') {
    return (
      <div className="audit-success">
        <FaCheck className="audit-success-icon" />
        <h2>Got it. Your audit is on the way.</h2>
        <p>
          We'll review {website} and send your missed-lead audit + custom
          AI-agent mockup to <strong>{email}</strong> within 48 hours.
        </p>
        <p className="audit-success-aside">
          No call. No follow-up sales pitch. Just the audit.
        </p>
        <p className="audit-success-spam-note">
          📬 Heads up: the audit comes by email and sometimes lands in{' '}
          <strong>Spam or Promotions</strong>. If you don&apos;t see it, check there
          and drag it to your inbox.
        </p>
      </div>
    );
  }

  return (
    <form id="sms-optin" className="audit-form" onSubmit={onSubmit} noValidate>
      <label className="audit-field">
        <span>Your business website</span>
        <input
          type="text"
          inputMode="url"
          placeholder="yourbusiness.com"
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
          required
          autoComplete="url"
        />
      </label>

      <label className="audit-field">
        <span>Your email</span>
        <input
          type="email"
          placeholder="you@yourbusiness.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          autoComplete="email"
        />
      </label>

      <label className="audit-field">
        <span>Business type</span>
        <select
          value={businessType}
          onChange={(e) => setBusinessType(e.target.value)}
          required
        >
          <option value="">Pick one…</option>
          {BUSINESS_TYPES.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
      </label>

      <label className="audit-field">
        <span>Best phone number</span>
        <input
          type="tel"
          inputMode="tel"
          placeholder="(310) 555-0123"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          required
          autoComplete="tel"
        />
      </label>

      <label className="audit-sms-consent">
        <input
          type="checkbox"
          checked={smsConsent}
          onChange={(e) => setSmsConsent(e.target.checked)}
        />
        <span>
          I agree to receive text messages from <strong>U Botika (UBOTIKA)</strong> at the mobile
          number provided, including replies about my free audit and occasional service
          updates. Consent is not a condition of purchase. Message frequency varies.
          Message &amp; data rates may apply. Reply <strong>STOP</strong> to opt out or{' '}
          <strong>HELP</strong> for help. See our{' '}
          <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
        </span>
      </label>

      <input
        type="text"
        name="website_url_confirm"
        tabIndex="-1"
        autoComplete="off"
        value={hp}
        onChange={(e) => setHp(e.target.value)}
        className="audit-hp"
        aria-hidden="true"
      />

      {errorMsg && <div className="audit-error">{errorMsg}</div>}

      <p className="audit-inline-proof">Avg. LA client saves $17,000/yr</p>

      <button
        type="submit"
        className="cta-button audit-submit"
        disabled={status === 'sending'}
      >
        {status === 'sending' ? 'Sending…' : 'Send me my free audit'}
      </button>

      <p className="audit-form-fine">
        We'll never spam you. One email with your audit, that's it.
      </p>
    </form>
  );
}

export default AuditForm;
